import Currency from '../../common/components/Currency.js'
import Checkbox from '../../common/components/Checkbox.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import {usesInventorySelector} from '../../data/company.js'
import {
  orderLinesSelector,
  orderLineIDsSelector,
  orderSelector,
} from '../../data/orders.js'
import {showEditOrderLineModal} from '../OrderListPage/Modals/EditOrderLineModal.js'
import {showConfirmOrderLineDeleteModal} from '../OrderListPage/Modals/ConfirmOrderLineDeleteModal.js'
import {updateOrderDetailForm} from './orderDetailActions.js'
import {
  orderDetailFormSelector,
  areAllLinesSelectedSelector,
  areIndeterminateLinesSelectedSelector,
} from './orderDetailSelectors.js'

import ProductRow from './ProductRow.js'
import {useSelector} from '../../store.js'
import {hasOrderFinancialsPermissionSelector} from '../../data/me.js'
import {showOrderTotalsModalModal} from '../OrderListPage/Modals/OrderTotalsModal.js'
import Icon from '../../common/icons/Icon.js'

export default function ProductInfo({orderNumber, readOnly}) {
  const form = useSelector(orderDetailFormSelector)
  const order = useSelector((state) => orderSelector(state, {orderNumber}))
  const lines = useSelector((state) => orderLinesSelector(state, {orderNumber}))
  const usesInventory = useSelector(usesInventorySelector)
  const areAllLinesSelected = useSelector(areAllLinesSelectedSelector)
  const areIndeterminateLinesSelected = useSelector(
    areIndeterminateLinesSelectedSelector,
  )
  const orderLineIDs = useSelector((state) =>
    orderLineIDsSelector(state, {orderNumber}),
  )
  const hasOrderFinancialsPermission = useSelector(
    hasOrderFinancialsPermissionSelector,
  )

  const baseColSpan =
    4 +
    (readOnly ? 0 : 1) +
    (usesInventory ? 1 : 0) +
    (hasOrderFinancialsPermission ? 3 : 0)

  return (
    <table className="table">
      <thead>
        <tr>
          {!readOnly && (
            <th className="table__th table__th--sm w-1">
              <Checkbox
                className="margin-bottom-0"
                checked={areAllLinesSelected}
                indeterminate={areIndeterminateLinesSelected}
                onClick={(checked) =>
                  updateOrderDetailForm({
                    selectedOrderLineIDs: checked ? orderLineIDs : [],
                  })
                }
              />
            </th>
          )}
          <th className="table__th table__th--sm th-width-25">Product</th>
          <th className="table__th table__th--sm th-width-15">
            {readOnly || form.selectedOrderLineIDs.length === 0 ? (
              'Addl Details'
            ) : (
              <ButtonLink
                className="darker lh-inherit align-right"
                title="Bulk Update Addl Details"
                onClick={() =>
                  showEditOrderLineModal(
                    orderNumber,
                    form.selectedOrderLineIDs,
                    'details',
                  )
                }
              >
                Addl Details
              </ButtonLink>
            )}
          </th>
          <th className="table__th table__th--sm th-width-15 align-right">
            {readOnly || form.selectedOrderLineIDs.length === 0 ? (
              'Ordered Qty'
            ) : (
              <ButtonLink
                className="darker lh-inherit align-right"
                title="Bulk Update Ordred Qty"
                onClick={() =>
                  showEditOrderLineModal(
                    orderNumber,
                    form.selectedOrderLineIDs,
                    'quantity',
                  )
                }
              >
                Ordered Qty
              </ButtonLink>
            )}
          </th>
          {usesInventory && (
            <th className="table__th table__th--sm th-width-10 align-right">
              Available
            </th>
          )}
          {hasOrderFinancialsPermission && (
            <>
              <th className="table__th table__th--sm th-width-10 align-right">
                {readOnly || form.selectedOrderLineIDs.length === 0 ? (
                  'Item Price'
                ) : (
                  <ButtonLink
                    className="darker lh-inherit align-right"
                    title="Bulk Update Item Price"
                    onClick={() =>
                      showEditOrderLineModal(
                        orderNumber,
                        form.selectedOrderLineIDs,
                        'item_price',
                      )
                    }
                  >
                    Item Price
                  </ButtonLink>
                )}
              </th>
              <th className="table__th table__th--sm th-width-10 align-right">
                {readOnly || form.selectedOrderLineIDs.length === 0 ? (
                  'Discount'
                ) : (
                  <ButtonLink
                    className="darker lh-inherit align-right"
                    title="Bulk Update Discount"
                    onClick={() =>
                      showEditOrderLineModal(
                        orderNumber,
                        form.selectedOrderLineIDs,
                        'discount_amount',
                      )
                    }
                  >
                    Discount
                  </ButtonLink>
                )}
              </th>
              <th className="table__th table__th--sm th-width-10 align-right">
                {readOnly || form.selectedOrderLineIDs.length === 0 ? (
                  'Total Price'
                ) : (
                  <ButtonLink
                    className="darker lh-inherit align-right"
                    title="Bulk Update Total Price"
                    onClick={() =>
                      showEditOrderLineModal(
                        orderNumber,
                        form.selectedOrderLineIDs,
                        'total_price',
                      )
                    }
                  >
                    Total Price
                  </ButtonLink>
                )}
              </th>
            </>
          )}
          <th className="table__th table__th--sm w-1">
            {form.isLoadingLines ? (
              <div className="loading align-center">
                <span className="spinner--x-sm v-align-middle" />
              </div>
            ) : !readOnly && form.selectedOrderLineIDs.length > 0 ? (
              <ButtonLink
                className="no-underline"
                title="Remove Lines"
                onClick={() =>
                  showConfirmOrderLineDeleteModal(
                    orderNumber,
                    form.selectedOrderLineIDs,
                  )
                }
              >
                <Icon name="trash" className="fs-00" />
              </ButtonLink>
            ) : (
              ''
            )}
          </th>
        </tr>
      </thead>
      <tbody className="table__tbody table__tbody--lines">
        {lines.map((line) => (
          <ProductRow
            key={line.link}
            line={line}
            orderNumber={orderNumber}
            readOnly={readOnly}
            orderLineIDs={orderLineIDs}
          />
        ))}
        <tr>
          {!readOnly && (
            <td
              className="table__td table__td--product-search"
              colSpan={baseColSpan}
            >
              <ButtonPrimary
                isOutlined
                size="sm"
                onClick={() => showEditOrderLineModal(orderNumber)}
              >
                Add a Product
              </ButtonPrimary>
            </td>
          )}
        </tr>
      </tbody>
      {hasOrderFinancialsPermission && (
        <tbody className="table__tbody table__tbody--totals">
          <tr>
            <td
              className="table__td fs-00 align-right"
              colSpan={baseColSpan - 2}
            >
              <strong className="v-align-base">Product Total</strong>
            </td>
            <td className="table__td fs-00" colSpan="2">
              {!readOnly ? (
                <button
                  className="btn btn--link darkest fs-00 v-align-base border-underline"
                  type="button"
                  onClick={() =>
                    showOrderTotalsModalModal(orderNumber, 'product_amount')
                  }
                >
                  <strong>
                    <Currency value={order.financial.product_amount} />
                  </strong>
                </button>
              ) : (
                <strong>
                  <Currency value={order.financial.product_amount} />
                </strong>
              )}
            </td>
          </tr>
          <tr>
            <td
              className="table__td fs-00 align-right"
              colSpan={baseColSpan - 2}
            >
              <strong className="v-align-base">Discount</strong>
            </td>
            <td className="table__td fs-00" colSpan="2">
              {!readOnly ? (
                <button
                  className="btn btn--link darker fs-00 v-align-base border-underline"
                  type="button"
                  onClick={() =>
                    showOrderTotalsModalModal(orderNumber, 'discount_amount')
                  }
                >
                  <Currency value={order.financial.discount_amount} />
                </button>
              ) : (
                <strong>
                  <Currency value={order.financial.discount_amount} />
                </strong>
              )}
            </td>
          </tr>
          <tr>
            <td
              className="table__td fs-00 align-right"
              colSpan={baseColSpan - 2}
            >
              <strong className="v-align-base">Shipping and Handling</strong>
            </td>
            <td className="table__td fs-00" colSpan="2">
              {!readOnly ? (
                <button
                  className="btn btn--link darker fs-00 v-align-base border-underline"
                  type="button"
                  onClick={() =>
                    showOrderTotalsModalModal(orderNumber, 'shipping_amount')
                  }
                >
                  <Currency value={order.financial.shipping_amount} />
                </button>
              ) : (
                <strong>
                  <Currency value={order.financial.shipping_amount} />
                </strong>
              )}
            </td>
          </tr>
          <tr>
            <td
              className="table__td fs-00 align-right"
              colSpan={baseColSpan - 2}
            >
              <strong className="v-align-base">Tax</strong>
            </td>
            <td className="table__td fs-00" colSpan="2">
              {!readOnly ? (
                <button
                  className="btn btn--link darker fs-00 v-align-base border-underline"
                  type="button"
                  onClick={() =>
                    showOrderTotalsModalModal(orderNumber, 'tax_amount')
                  }
                >
                  <Currency value={order.financial.tax_amount} />
                </button>
              ) : (
                <strong>
                  <Currency value={order.financial.tax_amount} />
                </strong>
              )}
            </td>
          </tr>
          <tr>
            <td
              className="table__td table__td--grand-total fs-00 align-right"
              colSpan={baseColSpan - 2}
            >
              <strong className="v-align-base">Grand Total</strong>
            </td>
            <td className="table__td table__td--grand-total fs-00" colSpan="2">
              {!readOnly ? (
                <button
                  className="btn btn--link darkest fs-00 v-align-base border-underline"
                  type="button"
                  onClick={() =>
                    showOrderTotalsModalModal(orderNumber, 'grand_total')
                  }
                >
                  <strong>
                    <Currency value={order.financial.grand_total} />
                  </strong>
                </button>
              ) : (
                <strong>
                  <Currency value={order.financial.grand_total} />
                </strong>
              )}
            </td>
          </tr>
        </tbody>
      )}
    </table>
  )
}
